// @owners { team: patients }
import { Card, LgPadding } from '@alto/design-system';
import React, { useMemo, useState } from 'react';
import { v4 as uuid } from 'uuid';
import { type OrderPricingQueryData } from '~shared/queries/useOrderPricing';
import { type Prescription } from '~shared/types';
import { useTrackPriceBreakdownViewed } from '../hooks/useTrackPriceBreakdownViewed';
import { PaymentBreakdown } from './PaymentBreakdown';

type Props = {
  readonly origin: string;
  readonly prescriptions: Prescription[];
  readonly orderPricing?: OrderPricingQueryData;
};

export const PricingBreakdownCard = ({ origin, orderPricing, prescriptions }: Props) => {
  // In split shipment scenario, only show prescriptions that belong to the current order (orderPricing is scoped to current order)
  const prescriptionsForCurrentOrder = useMemo((): Prescription[] => {
    if (!orderPricing?.item_pricing) return prescriptions;
    const idsInOrder = new Set(Object.keys(orderPricing.item_pricing).map(Number));
    return prescriptions.filter((p) => idsInOrder.has(p.id));
  }, [orderPricing?.item_pricing, prescriptions]);

  // key to include in analytics event to enable querying 'item viewed' events for given 'payment breakdown viewed' event.
  // Regenerate the id whenever the breakdown contents change (adjust state during render — no Effect needed).
  const [eventUuid, setEventUuid] = useState(() => uuid());
  const [prevPricingKey, setPrevPricingKey] = useState({ prescriptionsForCurrentOrder, orderPricing });
  if (
    prevPricingKey.prescriptionsForCurrentOrder !== prescriptionsForCurrentOrder ||
    prevPricingKey.orderPricing !== orderPricing
  ) {
    setPrevPricingKey({ prescriptionsForCurrentOrder, orderPricing });
    setEventUuid(uuid());
  }

  useTrackPriceBreakdownViewed({
    lineItems: orderPricing?.line_items,
    numberOfRxs: prescriptionsForCurrentOrder.length,
    origin,
    breakdownKey: eventUuid,
  });

  return (
    <Card>
      <LgPadding>
        <PaymentBreakdown
          orderPricing={orderPricing}
          prescriptions={prescriptionsForCurrentOrder}
          origin={origin}
          eventUuid={eventUuid}
        />
      </LgPadding>
    </Card>
  );
};
